import React, { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import AppSidebar from './Sidebar';
import Header from './Header';
import { usePreferenceActions } from '@/app/store/preferenceStore';
import { useAuthStore } from '@/app/store/authStore';
import axiosInstance from '@/app/api/axiosInstance';
import { toast } from 'sonner';
import { GlobalHotkeysProvider } from '@/app/components/GlobalHotkeysProvider';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { useIsMobile } from '@/hooks/use-mobile';

const MainLayout: React.FC = () => {
    const { loadPreferences } = usePreferenceActions();
    const { logoutAction } = useAuthStore();
    const navigate = useNavigate();
    const isMobile = useIsMobile();

    useEffect(() => {
        // Load user preferences when component mounts
        loadPreferences().catch((error) => {
            console.error('Failed to load preferences:', error);
        });
        
        // Add axios response interceptor to handle unauthorized errors
        const interceptorId = axiosInstance.interceptors.response.use(
            (response) => response,
            (error) => {
                // Check for unauthenticated error
                if (error?.response?.status === 401) {
                    toast.error('Session expired. Please login again.', {
                        id: 'session-expired',
                    });
                    logoutAction();
                    navigate('/login');
                }
                return Promise.reject(error);
            }
        );
        
        // Clean up interceptor on component unmount
        return () => {
            axiosInstance.interceptors.response.eject(interceptorId);
        };
    }, [loadPreferences, logoutAction, navigate]);

    return (
        <GlobalHotkeysProvider>
            {/* Sidebar state (expanded / icon rail) is persisted by shadcn in a cookie. */}
            <SidebarProvider
                className="h-screen min-h-0 overflow-hidden"
                // Slightly wider than the shadcn default so the deepest Admin
                // sub-items (e.g. "Razorpay Transactions") don't truncate.
                style={{ '--sidebar-width': '17rem' } as React.CSSProperties}
            >
                <AppSidebar />
                <SidebarInset className="min-w-0 overflow-hidden">
                    <Header isMobile={isMobile} />
                    <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
                        <Outlet /> {/* Child routes render here */}
                    </main>
                </SidebarInset>
            </SidebarProvider>
        </GlobalHotkeysProvider>
    );
};

export default MainLayout;
